import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, CalendarCheck } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/12902874/pexels-photo-12902874.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const HEADLINE_LINES = [
  { id: "line-1", content: "Empowering", gradient: false },
  { id: "line-2", content: "Businesses Through", gradient: false },
  { id: "line-3", content: "People and Technology", gradient: true },
];

const EASE_OUT = [0.22, 1, 0.36, 1];

export default function Hero() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 40]);

  return (
    <section
      id="home"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative hero-radial grid-overlay overflow-hidden pt-32 pb-20 lg:pt-44 lg:pb-28"
    >
      <motion.div
        style={{ y: glowY }}
        className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#F89335]/10 blur-[120px] pointer-events-none"
      />
      <div className="absolute bottom-0 -left-40 w-[420px] h-[420px] rounded-full bg-[#0a1160]/60 blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 lg:gap-10 items-center relative">
        <motion.div style={{ y: textY }}>
          <motion.span
            data-testid="hero-eyebrow"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE_OUT }}
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#F89335] border border-[#F89335]/30 rounded-full px-4 py-2 bg-[#F89335]/5"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#F89335] animate-pulse" />
            HR Solutions & Software Services
          </motion.span>

          <h1
            data-testid="hero-headline"
            className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08]"
          >
            {HEADLINE_LINES.map((line, i) => (
              <span key={line.id} className="block overflow-hidden pb-1 -mb-1">
                <motion.span
                  className={`block ${line.gradient ? "text-gradient-gold" : ""}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.13, ease: EASE_OUT }}
                >
                  {line.content}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subheading"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65, ease: EASE_OUT }}
            className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed max-w-xl"
          >
            At Krijo Consultancy Services, we bring clarity, capability, and creativity to your business challenges. Whether you're seeking the right talent or reliable technology, we offer end-to-end HR solutions and software services that help your business thrive.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8, ease: EASE_OUT }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <motion.a
              href="#services"
              data-testid="hero-get-started-button"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              className="btn-primary-glow font-['Poppins'] font-semibold px-8 py-4 rounded-xl inline-flex items-center gap-2"
            >
              Get Started <ArrowRight size={18} />
            </motion.a>
            <motion.a
              href="#contact"
              data-testid="hero-book-consultation-button"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              className="btn-ghost-glass font-['Poppins'] font-semibold px-8 py-4 rounded-xl inline-flex items-center gap-2 text-white"
            >
              <CalendarCheck size={18} /> Book a Consultation
            </motion.a>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.35, ease: EASE_OUT }}
          className="relative"
        >
          <motion.div
            style={{ y: imgY }}
            className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50"
          >
            <img
              src={HERO_IMG}
              alt="HR and technology professionals collaborating"
              className="w-full h-[320px] sm:h-[420px] object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#010433] via-[#010433]/20 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 glass-card rounded-2xl px-5 py-4 flex items-center gap-4">
              <img src="/krijo-logo.png" alt="Krijo logo mark" className="h-11 w-11 rounded-lg object-cover animate-pulse-ring" />
              <div>
                <p className="font-['Poppins'] font-semibold text-sm">People + Technology</p>
                <p className="text-xs text-slate-400">One partner. End-to-end delivery.</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
